import Sidebar from "@/components/Sidebar"
import { Section } from "@/model/CustomerSupport"
import { ROUTES } from "@/routes"
import cn from "@/utils/cn"
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react"
import { useLocation, useNavigate } from "react-router"

type SidebarLayoutProps = {
  children: ReactNode
}

type SectionContextType = {
  currentSection: Section
  currentSectionRootPath: string
  goBackToSectionRoot: () => void
}

const SectionContext = createContext<SectionContextType | null>(null)
export const useSectionContext = () => {
  const context = useContext(SectionContext)
  if (!context) {
    throw new Error("useSectionContext must be used within SidebarLayout")
  }
  return context
}

const sectionRoots: Record<Section, string> = {
  population: ROUTES.POPULATION.ROOT,
  support: ROUTES.SUPPORT.ROOT,
}

const SidebarLayout = ({ children }: SidebarLayoutProps) => {
  const navigate = useNavigate()
  const location = useLocation()

  const activeSection: Section = location.pathname.startsWith(
    ROUTES.SUPPORT.ROOT
  )
    ? "support"
    : "population"

  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const toggleSidebar = () => {
    setIsExpanded((prev: boolean) => !prev)
  }

  const [lastVisitedPaths, setLastVisitedPaths] =
    useState<Record<Section, string>>(sectionRoots)

  useEffect(() => {
    setLastVisitedPaths((prev) => ({
      ...prev,
      [activeSection]: location.pathname + location.search,
    }))
  }, [location.pathname, location.search, activeSection])

  const handleNavigateToSection = (section: Section) => {
    navigate(lastVisitedPaths[section])
  }

  const currentSectionRoot = sectionRoots[activeSection]
  const goBackToSectionRoot = () => navigate(currentSectionRoot)

  return (
    <SectionContext.Provider
      value={{
        currentSection: activeSection,
        currentSectionRootPath: currentSectionRoot,
        goBackToSectionRoot,
      }}
    >
      <div className="flex h-screen overflow-hidden">
        <Sidebar isExpanded={isExpanded} onToggle={toggleSidebar}>
          <Sidebar.Item
            icon="groups"
            label="Patient Population"
            active={activeSection === "population"}
            onClick={() => handleNavigateToSection("population")}
            isExpanded={isExpanded}
          />
          <Sidebar.Item
            icon="support_agent"
            label="Customer Support"
            active={activeSection === "support"}
            onClick={() => handleNavigateToSection("support")}
            isExpanded={isExpanded}
          />
        </Sidebar>

        <main
          className={cn(
            "flex-grow overflow-auto transition-all duration-300",
            isExpanded ? "ml-60" : "ml-16"
          )}
        >
          {children}
        </main>
      </div>
    </SectionContext.Provider>
  )
}

export default SidebarLayout
